/**
 * Generic "coming soon" placeholder for settings routes that are referenced in
 * the sidebar spec ([`ianfiles/sidebar-layout.md`](ianfiles/sidebar-layout.md))
 * but don't have a full implementation yet.
 *
 * Renders the PageHeader so deep links (and the breadcrumb) still look
 * correct, and clearly signals that the screen is a stub.
 */
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Construction } from "lucide-react";

export function SettingsStubPage({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <div className="space-y-6">
      <PageHeader title={title} description={description} />
      <Card>
        <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
          <div className="rounded-full bg-muted p-3 text-muted-foreground">
            <Construction className="h-6 w-6" />
          </div>
          <h2 className="text-base font-medium">Coming soon</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            This settings area is part of the navigation spec but hasn't been
            implemented yet. Pick another item from the sidebar to continue.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// Pre-built page components for each stubbed route so they can be imported
// directly in `App.tsx`.

export const MyCompanyPage = () => (
  <SettingsStubPage
    title="My Company"
    description="Public-facing company info shared with vendors and customers."
  />
);

export const DataMigrationPage = () => (
  <SettingsStubPage
    title="Data Migration"
    description="Import and migrate data from other systems."
  />
);

export const ProductDetailSettingsPage = () => (
  <SettingsStubPage
    title="Product Detail Page"
    description="Configure which sections and fields appear on product detail pages."
  />
);

export const QuickBooksConnectionPage = () => (
  <SettingsStubPage
    title="QuickBooks Connection"
    description="Connect or disconnect your QuickBooks Online account."
  />
);

export const QuickBooksAiWizardPage = () => (
  <SettingsStubPage
    title="QuickBooks AI Wizard"
    description="Use AI to help map QuickBooks data to OpenSupply."
  />
);

export const QuickBooksAiImportPage = () => (
  <SettingsStubPage
    title="QuickBooks AI Import"
    description="Bulk-import QuickBooks records with AI-assisted matching."
  />
);

export const QuickBooksSettingsPage = () => (
  <SettingsStubPage
    title="QuickBooks Settings"
    description="Sync preferences and field mappings for QuickBooks."
  />
);

export const BankFeedsPage = () => (
  <SettingsStubPage
    title="Bank Feeds"
    description="Connect bank accounts to import transactions automatically."
  />
);

export const AdminOrganizationsPage = () => (
  <SettingsStubPage
    title="Organizations"
    description="Manage all organizations on this OpenSupply instance."
  />
);

export const AdminAiUsagePage = () => (
  <SettingsStubPage
    title="AI Usage"
    description="Per-organization AI usage and quota."
  />
);

export const AdminSchemaExplorerPage = () => (
  <SettingsStubPage
    title="Schema Explorer"
    description="Inspect the OpenSupply database schema."
  />
);

export const AdminWorkflowsPage = () => (
  <SettingsStubPage
    title="System Workflows"
    description="Built-in workflows that power OpenSupply automations."
  />
);

export const AdminAiWorkflowsPage = () => (
  <SettingsStubPage
    title="AI Workflows"
    description="Workflows that use AI to classify, extract, and route records."
  />
);

export const AdminQboWebhookPage = () => (
  <SettingsStubPage
    title="QuickBooks Webhook"
    description="Inspect and replay QuickBooks Online webhook events."
  />
);
